import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LocateFixed } from "lucide-react";
import { toast } from "sonner";
import { PageHero, CategoryChips, ProvinceSelect, SearchInput } from "./PageParts";
import { ListingCard } from "./ListingCard";

export const ListingPage = ({ mode, fetcher, hero, emptyText, note }) => {
  const [params] = useSearchParams();
  const [f, setF] = useState({ category: params.get("category") || "", province: params.get("province") || "", q: "" });
  const [geo, setGeo] = useState(null);
  const [data, setData] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    const t = setTimeout(() => fetcher({ ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), ...(geo || {}) })
      .then(setData).catch(() => setData({ categories: [], items: [] })), 250);
    return () => clearTimeout(t);
  }, [f, geo, fetcher]);
  const nearMe = () => {
    if (geo) return setGeo(null);
    if (!navigator.geolocation) return toast.error("Location isn't available in this browser");
    navigator.geolocation.getCurrentPosition((p) => setGeo({ lat: p.coords.latitude, lng: p.coords.longitude }), () => toast.message("Location not shared — choose a province instead"));
  };
  return (
    <div data-testid={`${mode}-page`}>
      <PageHero {...hero} testId={`${mode}-hero`}>
        {hero.cta && <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={() => document.getElementById(`${mode}-list`)?.scrollIntoView({ behavior: "smooth" })} className="btn-green inline-flex items-center rounded-full px-6 py-3 text-sm font-semibold" data-testid={`${mode}-hero-cta`}>{hero.cta}</button>
          {hero.cta2 && <button onClick={() => document.getElementById(`${mode}-list`)?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center rounded-full border border-white/60 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10" data-testid={`${mode}-hero-cta2`}>{hero.cta2}</button>}
        </div>}
      </PageHero>
      <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="flex flex-wrap gap-3">
          <SearchInput value={f.q} onChange={(v) => set("q", v)} placeholder="Search by name, organization or town" testId={`${mode}-search`} />
          <ProvinceSelect value={f.province} onChange={(v) => set("province", v)} testId={`${mode}-province`} />
          <button onClick={nearMe} data-testid={`${mode}-near-me`} className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold ${geo ? "btn-green" : "bg-[#1E3A8A] text-white"}`}>
            <LocateFixed size={16} />{geo ? "Near me ✕" : "Near me"}
          </button>
        </div>
        <div className="mt-4"><CategoryChips cats={data?.categories || []} value={f.category} onChange={(v) => set("category", v)} prefix={mode} /></div>
        <p id={`${mode}-list`} className="mt-8 scroll-mt-24 text-sm text-slate-600" data-testid={`${mode}-count`}>{data ? `${data.items.length} listed` : "Loading…"}</p>
        <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {data === null && [...Array(6)].map((_, i) => <div key={i} className="h-96 animate-pulse rounded-3xl bg-white" />)}
          {data?.items.map((o) => <ListingCard key={o.id} o={o} mode={mode} />)}
        </div>
        {data?.items.length === 0 && <p className="rounded-3xl border border-dashed border-slate-300 bg-white py-14 text-center text-slate-600" data-testid={`${mode}-empty`}>{emptyText}</p>}
        <p className="mt-10 text-xs text-slate-600">{note}</p>
      </div>
    </div>
  );
};
